import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Settings, LogOut, Loader2 } from 'lucide-react';
import { useAuth } from '../../hooks/useAuth';
import { useSession } from '../../hooks/useSession';
import { LogoutConfirmDialog } from '../dialogs/LogoutConfirmDialog';
import { cn } from '../../utils/cn';

interface UserMenuProps {
  size?: 'sm' | 'md';
  align?: 'left' | 'right';
}

export const UserMenu: React.FC<UserMenuProps> = ({ size = 'sm', align = 'right' }) => {
  const { user, logout } = useAuth();
  const { activeSessions, terminateAllSessions } = useSession();
  const navigate = useNavigate();
  
  const [isOpen, setIsOpen] = useState(false);
  const [isLogoutOpen, setIsLogoutOpen] = useState(false);
  const [isDestroying, setIsDestroying] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  const displayName = user?.firstName || user?.email?.split('@')[0] || '';
  const initials = displayName ? displayName[0].toUpperCase() : 'U';
    
  // SessionView uses 2-char initials from email
  const displayInitials = size === 'sm' 
    ? (user?.email?.slice(0, 2).toUpperCase() || 'U')
    : initials;

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleLogoutClick = () => {
    setIsOpen(false);
    setIsLogoutOpen(true);
  };

  const handleConfirmLogout = async () => {
    setIsLogoutOpen(false);
    if (activeSessions.size > 0) {
      setIsDestroying(true);
      try {
        await terminateAllSessions();
      } catch (err) {
        console.error('Failed to terminate sessions on logout', err);
      }
    }
    logout();
  };

  return (
    <div className="relative inline-block text-left" ref={menuRef}>
      {/* Trigger button */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className={cn(
          "flex items-center justify-center font-bold shadow-sm transition-all focus:outline-none",
          size === 'md'
            ? "w-12 h-12 rounded-[1.25rem] bg-navy text-white font-display text-lg shadow-lg rotate-2 hover:opacity-90 active:scale-95"
            : "w-8 h-8 rounded-full bg-primary text-on-primary text-xs shadow-sm hover:opacity-90 active:scale-95"
        )}
        title="User Menu"
      >
        {displayInitials}
      </button>

      {/* Dropdown Menu */}
      {isOpen && (
        <div
          className={cn(
            "absolute mt-2 w-48 rounded-2xl bg-surface-container-lowest/95 backdrop-blur-md border border-outline-variant/10 shadow-ambient p-1.5 z-[99] flex flex-col gap-0.5 animate-in fade-in slide-in-from-top-2 duration-200",
            align === 'right' ? "right-0" : "left-0"
          )}
        >
          {/* User Info Header on Mobile */}
          <div className="px-3 py-2 text-left sm:hidden">
            <p className="text-xs font-black text-navy uppercase tracking-wider truncate">
              {displayName}
            </p>
            <p className="text-[9px] font-medium text-secondary italic">
              {user?.role === 'customer_user' ? 'Executive Admin' : 'Agent Operator'}
            </p>
          </div>
          
          <button
            onClick={() => {
              setIsOpen(false);
              navigate('/settings');
            }}
            className="flex items-center gap-2.5 w-full px-3 py-2.5 rounded-xl text-left text-xs font-bold text-secondary hover:bg-surface-container hover:text-on-surface transition-all duration-200"
          >
            <Settings size={16} />
            <span>Change Password</span>
          </button>
          
          <div className="h-[1px] bg-outline-variant/10 my-0.5" />
          
          <button
            onClick={handleLogoutClick}
            className="flex items-center gap-2.5 w-full px-3 py-2.5 rounded-xl text-left text-xs font-bold text-error/85 hover:bg-error-container/20 hover:text-error transition-all duration-200"
          >
            <LogOut size={16} />
            <span>Log Off</span>
          </button>
        </div>
      )}

      <LogoutConfirmDialog
        isOpen={isLogoutOpen}
        onCancel={() => setIsLogoutOpen(false)}
        onConfirm={handleConfirmLogout}
      />

      {isDestroying && (
        <div className="fixed inset-0 bg-surface/60 backdrop-blur-md z-[9999] flex flex-col items-center justify-center animate-fade-in">
          <div className="bg-surface-container-lowest/80 border border-outline-variant/15 p-8 rounded-3xl shadow-ambient max-w-sm w-full text-center flex flex-col items-center gap-4">
            <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center">
              <Loader2 className="w-6 h-6 text-primary animate-spin" />
            </div>
            <div>
              <h3 className="font-display font-bold text-navy text-base">Terminating sessions...</h3>
              <p className="text-secondary text-xs mt-1">
                Securely cleaning agent conversations.
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
export default UserMenu;
